import { CSSProperties, FC, ReactNode, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Divider, Dropdown, Menu } from 'antd';
import type { DropdownProps } from 'antd';
import { Tags as TagsIcon } from 'lucide-react';
import { TbKeyframe, TbPlus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSubjectTags, useTags, useToggleTagSubject } from '@/queries';
import DropdownOptionContent from '@/shared/components/dropdowns/DropdownOptionContent';
import DropdownPanel, { DropdownMenuLabel, DropdownPanelMenu } from '@/shared/components/dropdowns/DropdownPanel';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import WorkspacePrimitiveEditorModal from '@/shared/components/modals/ResourceCreationModals/WorkspacePrimitiveEditorModal';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { SubjectReference, Tag } from '@/types';

interface TagAssignmentDropdownProps {
    anchorStyle?: CSSProperties;
    children: ReactNode;
    disabled?: boolean;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
    placement?: DropdownProps['placement'];
    resourceLabel: string;
    subject: SubjectReference;
    trigger?: DropdownProps['trigger'];
}

const normalizedSearchMatch = (tag: Tag, search: string) => {
    const normalizedSearch = search.trim().toLowerCase();

    return (
        !normalizedSearch ||
        [tag.name, tag.visibility].some((value) => String(value).toLowerCase().includes(normalizedSearch))
    );
};

const uniqueTags = (tags: Tag[]) => [...new Map(tags.map((tag) => [tag.id, tag])).values()];

const resourceCountLabel = (assignmentCount: number) =>
    `${assignmentCount.toLocaleString()} ${assignmentCount === 1 ? 'Resource' : 'Resources'}`;

const TagAssignmentDropdown: FC<TagAssignmentDropdownProps> = ({
    anchorStyle,
    children,
    disabled,
    onOpenChange,
    open,
    placement = 'bottomLeft',
    resourceLabel,
    subject,
    trigger = ['click'],
}) => {
    const { token } = useAntDesignTheme();
    const [internalOpen, setInternalOpen] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [search, setSearch] = useState('');
    const resolvedOpen = open ?? internalOpen;
    const tagsQuery = useTags();
    const subjectTagsQuery = useSubjectTags(subject);
    const toggleTag = useToggleTagSubject(subject);
    const selectedTags = useMemo(() => uniqueTags(subjectTagsQuery.data ?? []), [subjectTagsQuery.data]);
    const selectedById = useMemo(() => new Map(selectedTags.map((tag) => [tag.id, tag])), [selectedTags]);
    const allTags = useMemo(
        () => uniqueTags([...(tagsQuery.data ?? []), ...selectedTags]),
        [selectedTags, tagsQuery.data],
    );
    const filteredSelectedTags = useMemo(
        () => selectedTags.filter((tag) => normalizedSearchMatch(tag, search)),
        [search, selectedTags],
    );
    const remainingTags = useMemo(
        () => allTags.filter((tag) => !selectedById.has(tag.id) && normalizedSearchMatch(tag, search)),
        [allTags, search, selectedById],
    );
    const loading = tagsQuery.isLoading || subjectTagsQuery.isLoading;

    const setResolvedOpen = (nextOpen: boolean) => {
        if (open === undefined) {
            setInternalOpen(nextOpen);
        }

        onOpenChange?.(nextOpen);

        if (!nextOpen) {
            setSearch('');
        }
    };

    const toggle = async (tag: Tag) => {
        if (toggleTag.isPending) {
            return;
        }

        const selectedTag = selectedById.get(tag.id);

        try {
            await toggleTag.mutateAsync({
                assignmentId: selectedTag?.assignmentId,
                selected: Boolean(selectedTag),
                tagId: tag.id,
            });
        } catch (error) {
            captureException(error);
            toast.error('Unable to update tags for this resource.');
        }
    };

    const renderTag = (tag: Tag, selected: boolean) => (
        <Menu.Item
            disabled={toggleTag.isPending}
            key={tag.id}
            onClick={({ domEvent }) => {
                domEvent.stopPropagation();
                void toggle(tag);
            }}
        >
            <DropdownOptionContent
                avatar={{
                    icon: <TbKeyframe color={tag.color ?? token.colorTextTertiary} size='0.875rem' />,
                }}
                metadata={[
                    tag.visibility,
                    ...(tag.parentId ? ['Nested Tag'] : []),
                    resourceCountLabel(tag.assignmentCount),
                ]}
                selected={selected}
                singleLine
                title={tag.name}
            />
        </Menu.Item>
    );

    const renderEmptyView = ({ description, icon, label }: { description: string; icon: ReactNode; label: string }) => (
        <EmptyView description={description} height='10rem' icon={icon} label={label} />
    );

    return (
        <>
            <Dropdown
                arrow={false}
                destroyOnHidden
                disabled={disabled}
                mouseEnterDelay={0.1}
                mouseLeaveDelay={0.2}
                onOpenChange={(nextOpen, info) => {
                    if (!nextOpen && info.source === 'menu') {
                        return;
                    }

                    setResolvedOpen(nextOpen);
                }}
                open={resolvedOpen}
                popupRender={() => (
                    <DropdownPanel
                        actions={
                            <DropdownPanelMenu selectable={false}>
                                <Menu.Item
                                    disabled={toggleTag.isPending}
                                    icon={<TbPlus size='1rem' />}
                                    key='create-tag'
                                    onClick={() => {
                                        setResolvedOpen(false);
                                        setCreateOpen(true);
                                    }}
                                >
                                    Create Tag
                                </Menu.Item>
                            </DropdownPanelMenu>
                        }
                        footer={<LearnMoreFooter topic='tags' variant='dropdown' />}
                        maxHeight='min(32rem, 75vh)'
                        search={{
                            ariaLabel: `Search tags for ${resourceLabel}`,
                            placeholder: 'Search Tags...',
                            setValue: setSearch,
                            value: search,
                        }}
                        width='min(24rem, calc(100vw - 2rem))'
                    >
                        {loading ? (
                            <div style={{ color: token.colorTextTertiary, padding: '2rem', textAlign: 'center' }}>
                                <small>Loading tags...</small>
                            </div>
                        ) : (
                            <>
                                <DropdownMenuLabel
                                    extra={<small>{filteredSelectedTags.length.toLocaleString()}</small>}
                                    icon={<TagsIcon size='0.75rem' />}
                                >
                                    Tags Added to Resource
                                </DropdownMenuLabel>
                                {filteredSelectedTags.length ? (
                                    <DropdownPanelMenu selectable={false}>
                                        {filteredSelectedTags.map((tag) => renderTag(tag, true))}
                                    </DropdownPanelMenu>
                                ) : (
                                    renderEmptyView({
                                        description: search
                                            ? 'No tags added to this resource match this search.'
                                            : 'No tags are assigned to this resource.',
                                        icon: <TagsIcon size='1.25rem' />,
                                        label: search ? 'No Matching Tags Added' : 'No Tags Added to Resource',
                                    })
                                )}
                                <Divider style={{ margin: 0 }} />
                                <DropdownMenuLabel
                                    extra={<small>{remainingTags.length.toLocaleString()}</small>}
                                    icon={<TbKeyframe size='0.875rem' />}
                                >
                                    Other Tags
                                </DropdownMenuLabel>
                                {remainingTags.length ? (
                                    <DropdownPanelMenu selectable={false}>
                                        {remainingTags.map((tag) => renderTag(tag, false))}
                                    </DropdownPanelMenu>
                                ) : (
                                    renderEmptyView({
                                        description: search
                                            ? 'No other tags match this search.'
                                            : allTags.length
                                              ? 'All available tags are assigned to this resource.'
                                              : 'Create a tag to start classifying this resource.',
                                        icon: <TbKeyframe size='1.25rem' />,
                                        label: search ? 'No Other Tags Found' : 'No Other Tags',
                                    })
                                )}
                            </>
                        )}
                    </DropdownPanel>
                )}
                placement={placement}
                trigger={trigger}
            >
                <span style={{ display: 'inline-flex', minWidth: 0, ...anchorStyle }}>{children}</span>
            </Dropdown>
            <WorkspacePrimitiveEditorModal
                availableTags={allTags}
                errorMessage='Unable to create and attach this tag.'
                kind='tag'
                open={createOpen}
                subjectsToAttach={[subject]}
                submitLabel='Create and Attach'
                successMessage='Tag created and attached.'
                onClose={() => setCreateOpen(false)}
            />
        </>
    );
};

export type { TagAssignmentDropdownProps };
export default TagAssignmentDropdown;
